import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText } from 'react-native-svg';
import Footer from '../components/Footer';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const colors = {
  primary: '#4F46E5',
  background: '#FFFFFF',
  surface: '#F8FAFC',
  cardBackground: '#FFFFFF',
  border: '#E2E8F0',
  text: '#1E293B',
  textSecondary: '#64748B',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

const DownPaymentCalculatorScreen = () => {
  const [propertyValue, setPropertyValue] = useState('1000000');
  const [downPaymentPercentage, setDownPaymentPercentage] = useState(20);
  const [downPaymentAmount, setDownPaymentAmount] = useState('200000');
  const [propertyType, setPropertyType] = useState('home');
  const [currentSavings, setCurrentSavings] = useState('50000');
  const [monthlySavings, setMonthlySavings] = useState('15000');
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyIncome, setMonthlyIncome] = useState('75000');
  const [showFAQ, setShowFAQ] = useState(false);
  const [showAffordability, setShowAffordability] = useState(false);
  const [showSavingsTimeline, setShowSavingsTimeline] = useState(false);
  const [results, setResults] = useState(null);
  const [displayedRows, setDisplayedRows] = useState(5);

  const propertyTypes = [
    { label: 'Home/Apartment', value: 'home', minDownPayment: 10, maxDownPayment: 50 },
    { label: 'Commercial Property', value: 'commercial', minDownPayment: 25, maxDownPayment: 60 },
    { label: 'Car/Vehicle', value: 'car', minDownPayment: 10, maxDownPayment: 40 },
    { label: 'Investment Property', value: 'investment', minDownPayment: 20, maxDownPayment: 50 },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateDownPayment = () => {
    const propertyVal = parseFloat(propertyValue) || 0;
    const downPaymentPercent = parseFloat(downPaymentPercentage) || 0;
    const currentSav = parseFloat(currentSavings) || 0;
    const monthlySav = parseFloat(monthlySavings) || 0;
    const rate = parseFloat(interestRate) || 0;
    const tenure = parseFloat(loanTenure) || 0;
    const income = parseFloat(monthlyIncome) || 0;

    // Calculate down payment amount
    const downPayment = (propertyVal * downPaymentPercent) / 100;
    
    // Calculate loan amount
    const loanAmount = propertyVal - downPayment;
    
    // Calculate EMI
    const monthlyRate = rate / (12 * 100);
    const totalMonths = tenure * 12;
    const emi = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / 
                (Math.pow(1 + monthlyRate, totalMonths) - 1);
    
    // Calculate total interest and total payment
    const totalPayment = emi * totalMonths;
    const totalInterest = totalPayment - loanAmount;
    
    // Calculate LTV ratio
    const ltvRatio = (loanAmount / propertyVal) * 100;
    
    // Calculate savings timeline
    const shortfall = Math.max(0, downPayment - currentSav);
    const monthsToSave = shortfall > 0 ? Math.ceil(shortfall / monthlySav) : 0;
    
    // Calculate affordability
    const debtToIncomeRatio = (emi / income) * 100;
    const affordabilityStatus = debtToIncomeRatio <= 40 ? 'Good' : 
                              debtToIncomeRatio <= 50 ? 'Moderate' : 'High Risk';
    
    // Calculate closing costs (estimated)
    const closingCosts = propertyVal * 0.02; // 2% of property value
    
    // Calculate total upfront costs
    const totalUpfrontCosts = downPayment + closingCosts;

    return {
      propertyValue: propertyVal,
      downPayment,
      downPaymentPercentage: downPaymentPercent,
      loanAmount,
      emi,
      totalPayment,
      totalInterest,
      ltvRatio,
      shortfall,
      monthsToSave,
      debtToIncomeRatio,
      affordabilityStatus,
      closingCosts,
      totalUpfrontCosts,
      currentSavings: currentSav,
      monthlySavings: monthlySav,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateDownPayment();
    setResults(calculatedResults);
    
    // Update down payment amount when percentage changes
    const newDownPayment = (parseFloat(propertyValue) * downPaymentPercentage) / 100;
    setDownPaymentAmount(newDownPayment.toString());
  }, [propertyValue, downPaymentPercentage, currentSavings, monthlySavings, interestRate, loanTenure, monthlyIncome]);

  const handleDownPaymentAmountChange = (value) => {
    setDownPaymentAmount(value);
    const propertyVal = parseFloat(propertyValue) || 0;
    const downPaymentVal = parseFloat(value) || 0;
    if (propertyVal > 0) {
      const percentage = (downPaymentVal / propertyVal) * 100;
      setDownPaymentPercentage(Math.min(100, Math.max(0, percentage)));
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>Down Payment Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your down payment requirements and savings timeline
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>Down Payment Details</Text>
      
      {/* Property Type Selection */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Property Type</Text>
        <View style={styles.segmentedControl}>
          {propertyTypes.map((type) => (
            <TouchableOpacity
              key={type.value}
              style={[
                styles.segmentButton,
                propertyType === type.value && styles.segmentButtonActive,
              ]}
              onPress={() => setPropertyType(type.value)}
            >
              <Text
                style={[
                  styles.segmentButtonText,
                  propertyType === type.value && styles.segmentButtonTextActive,
                ]}
              >
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Property Value */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Property Value</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={propertyValue}
            onChangeText={setPropertyValue}
            placeholder="Enter property value"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Down Payment Percentage */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>
          Down Payment Percentage: {downPaymentPercentage.toFixed(1)}%
        </Text>
        <Slider
          style={styles.slider}
          minimumValue={propertyTypes.find(p => p.value === propertyType)?.minDownPayment || 10}
          maximumValue={propertyTypes.find(p => p.value === propertyType)?.maxDownPayment || 50}
          value={downPaymentPercentage}
          onValueChange={setDownPaymentPercentage}
          step={0.5}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>
            {propertyTypes.find(p => p.value === propertyType)?.minDownPayment || 10}%
          </Text>
          <Text style={styles.sliderLabel}>
            {propertyTypes.find(p => p.value === propertyType)?.maxDownPayment || 50}%
          </Text>
        </View>
      </View>

      {/* Down Payment Amount */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Down Payment Amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={downPaymentAmount}
            onChangeText={handleDownPaymentAmountChange}
            placeholder="Enter down payment amount"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Current Savings */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Current Savings</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={currentSavings}
            onChangeText={setCurrentSavings}
            placeholder="Enter current savings"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Monthly Savings */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Monthly Savings Capacity</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={monthlySavings}
            onChangeText={setMonthlySavings}
            placeholder="Enter monthly savings"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Loan Details */}
      <Text style={[styles.cardTitle, { marginTop: 30 }]}>Loan Details</Text>

      {/* Interest Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Interest Rate: {interestRate.toFixed(1)}% per annum</Text>
        <Slider
          style={styles.slider}
          minimumValue={6}
          maximumValue={18}
          value={interestRate}
          onValueChange={setInterestRate}
          step={0.1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>6%</Text>
          <Text style={styles.sliderLabel}>18%</Text>
        </View>
      </View>

      {/* Loan Tenure */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Loan Tenure: {loanTenure} years</Text>
        <Slider
          style={styles.slider}
          minimumValue={5}
          maximumValue={30}
          value={loanTenure}
          onValueChange={setLoanTenure}
          step={1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>5 years</Text>
          <Text style={styles.sliderLabel}>30 years</Text>
        </View>
      </View>

      {/* Monthly Income */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Monthly Income</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={monthlyIncome}
            onChangeText={setMonthlyIncome}
            placeholder="Enter monthly income"
            keyboardType="numeric"
          />
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { downPayment, loanAmount } = results;
    const total = downPayment + loanAmount;
    const downPaymentPercentage = (downPayment / total) * 100;
    const loanPercentage = (loanAmount / total) * 100;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    
    const downPaymentStroke = (downPaymentPercentage / 100) * circumference;
    const loanStroke = (loanPercentage / 100) * circumference;

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Payment Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={`${downPaymentStroke} ${circumference}`}
              strokeDashoffset={0}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={`${loanStroke} ${circumference}`}
              strokeDashoffset={-downPaymentStroke}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              Total Value
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="14"
              fill={colors.textSecondary}
            >
              {formatCurrency(total)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.legendContainer}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Down Payment ({downPaymentPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Loan Amount ({loanPercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>Calculation Results</Text>
        
        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>Down Payment Required</Text>
          <Text style={styles.resultValue}>{formatCurrency(results.downPayment)}</Text>
        </View>
        
        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>Loan Amount</Text>
          <Text style={styles.resultValue}>{formatCurrency(results.loanAmount)}</Text>
        </View>
        
        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>Monthly EMI</Text>
          <Text style={styles.resultValue}>{formatCurrency(results.emi)}</Text>
        </View>
        
        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>LTV Ratio</Text>
          <Text style={styles.resultValue}>{results.ltvRatio.toFixed(1)}%</Text>
        </View>
        
        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>Closing Costs (Est.)</Text>
          <Text style={styles.resultValue}>{formatCurrency(results.closingCosts)}</Text>
        </View>
        
        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>Total Upfront Costs</Text>
          <Text style={[styles.resultValue, styles.totalValue]}>{formatCurrency(results.totalUpfrontCosts)}</Text>
        </View>

        {results.shortfall > 0 && (
          <>
            <View style={styles.resultRow}>
              <Text style={styles.resultLabel}>Savings Shortfall</Text>
              <Text style={[styles.resultValue, { color: colors.warning }]}>
                {formatCurrency(results.shortfall)}
              </Text>
            </View>
            
            <View style={styles.resultRow}>
              <Text style={styles.resultLabel}>Months to Save</Text>
              <Text style={[styles.resultValue, { color: colors.warning }]}>
                {results.monthsToSave} months
              </Text>
            </View>
          </>
        )}
      </View>
    );
  };

  const renderAffordabilityAnalysis = () => (
    <View style={styles.fullWidthSection}>
      <TouchableOpacity
        style={styles.sectionHeader}
        onPress={() => setShowAffordability(!showAffordability)}
      >
        <Text style={styles.sectionTitle}>Affordability Analysis</Text>
        <Ionicons
          name={showAffordability ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>
      
      {showAffordability && results && (
        <View style={styles.sectionContent}>
          <View style={styles.affordabilityCard}>
            <View style={styles.affordabilityHeader}>
              <Text style={styles.affordabilityTitle}>Debt-to-Income Analysis</Text>
              <View style={[
                styles.affordabilityBadge,
                {
                  backgroundColor: results.affordabilityStatus === 'Good' ? colors.success :
                                 results.affordabilityStatus === 'Moderate' ? colors.warning : colors.error
                }
              ]}>
                <Text style={styles.affordabilityBadgeText}>{results.affordabilityStatus}</Text>
              </View>
            </View>
            
            <View style={styles.affordabilityMetric}>
              <Text style={styles.metricLabel}>Monthly Income</Text>
              <Text style={styles.metricValue}>{formatCurrency(parseFloat(monthlyIncome))}</Text>
            </View>
            
            <View style={styles.affordabilityMetric}>
              <Text style={styles.metricLabel}>Monthly EMI</Text>
              <Text style={styles.metricValue}>{formatCurrency(results.emi)}</Text>
            </View>
            
            <View style={styles.affordabilityMetric}>
              <Text style={styles.metricLabel}>Debt-to-Income Ratio</Text>
              <Text style={[
                styles.metricValue,
                {
                  color: results.debtToIncomeRatio <= 40 ? colors.success :
                        results.debtToIncomeRatio <= 50 ? colors.warning : colors.error
                }
              ]}>
                {results.debtToIncomeRatio.toFixed(1)}%
              </Text>
            </View>
            
            <View style={styles.recommendationBox}>
              <Text style={styles.recommendationTitle}>Recommendation</Text>
              <Text style={styles.recommendationText}>
                {results.debtToIncomeRatio <= 40
                  ? "Your debt-to-income ratio is excellent. You should be able to comfortably afford this loan."
                  : results.debtToIncomeRatio <= 50
                  ? "Your debt-to-income ratio is moderate. Consider reducing other debts or increasing income."
                  : "Your debt-to-income ratio is high. Consider a larger down payment or lower property value."}
              </Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );

  const renderSavingsTimeline = () => (
    <View style={styles.fullWidthSection}>
      <TouchableOpacity
        style={styles.sectionHeader}
        onPress={() => setShowSavingsTimeline(!showSavingsTimeline)}
      >
        <Text style={styles.sectionTitle}>Savings Timeline</Text>
        <Ionicons
          name={showSavingsTimeline ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>
      
      {showSavingsTimeline && results && (
        <View style={styles.sectionContent}>
          <View style={styles.timelineCard}>
            <Text style={styles.timelineTitle}>Savings Progress</Text>
            
            <View style={styles.progressContainer}>
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill,
                    { 
                      width: `${Math.min(100, (results.currentSavings / results.downPayment) * 100)}%`,
                      backgroundColor: results.shortfall === 0 ? colors.success : colors.primary
                    }
                  ]}
                />
              </View>
              <Text style={styles.progressText}>
                {((results.currentSavings / results.downPayment) * 100).toFixed(1)}% Complete
              </Text>
            </View>
            
            <View style={styles.timelineMetrics}>
              <View style={styles.timelineMetric}>
                <Text style={styles.timelineLabel}>Current Savings</Text>
                <Text style={styles.timelineValue}>{formatCurrency(results.currentSavings)}</Text>
              </View>
              
              <View style={styles.timelineMetric}>
                <Text style={styles.timelineLabel}>Target Amount</Text>
                <Text style={styles.timelineValue}>{formatCurrency(results.downPayment)}</Text>
              </View>
              
              {results.shortfall > 0 && (
                <>
                  <View style={styles.timelineMetric}>
                    <Text style={styles.timelineLabel}>Amount Needed</Text>
                    <Text style={[styles.timelineValue, { color: colors.warning }]}>
                      {formatCurrency(results.shortfall)}
                    </Text>
                  </View>
                  
                  <View style={styles.timelineMetric}>
                    <Text style={styles.timelineLabel}>Time to Goal</Text>
                    <Text style={[styles.timelineValue, { color: colors.primary }]}>
                      {results.monthsToSave} months
                    </Text>
                  </View>
                </>
              )}
            </View>
            
            {results.shortfall === 0 && (
              <View style={styles.successMessage}>
                <Ionicons name="checkmark-circle" size={24} color={colors.success} />
                <Text style={styles.successText}>
                  Congratulations! You have enough savings for the down payment.
                </Text>
              </View>
            )}
          </View>
        </View>
      )}
    </View>
  );

  const renderInformation = () => (
    <View style={styles.fullWidthSection}>
      <View style={styles.infoContainer}>
        <Text style={styles.infoTitle}>Down Payment Calculator</Text>
        <Text style={styles.infoSubtitle}>Make informed decisions about your property purchase</Text>
        
        <Text style={styles.infoText}>
          A down payment calculator helps you determine how much money you need upfront when purchasing 
          a property. It considers various factors including property value, loan terms, and your 
          financial capacity to provide comprehensive insights for your purchase decision.
        </Text>
        
        <Text style={styles.infoTitle}>Key Features</Text>
        <Text style={styles.infoText}>
          • Calculate down payment requirements for different property types{'\n'}
          • Analyze loan-to-value ratios and affordability{'\n'}
          • Track savings progress and timeline to reach your goal{'\n'}
          • Estimate closing costs and total upfront expenses{'\n'}
          • Compare different down payment scenarios{'\n'}
          • Get personalized recommendations based on your financial situation
        </Text>
        
        <Text style={styles.infoTitle}>How to Use Down Payment Calculator?</Text>
        <Text style={styles.infoText}>
          1. Select your property type (home, commercial, car, or investment){'\n'}
          2. Enter the total property value{'\n'}
          3. Set your desired down payment percentage{'\n'}
          4. Input your current savings and monthly savings capacity{'\n'}
          5. Specify loan details including interest rate and tenure{'\n'}
          6. Review the comprehensive analysis and recommendations
        </Text>
        
        <View style={styles.tipBox}>
          <Ionicons name="bulb-outline" size={20} color={colors.warning} />
          <Text style={styles.tipText}>
            Tip: A higher down payment reduces your loan amount, monthly EMI, and total interest paid. 
            However, ensure you maintain adequate emergency funds after making the down payment.
          </Text>
        </View>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is a down payment?",
        answer: "A down payment is the upfront cash payment you make when purchasing a property. It's typically expressed as a percentage of the total property value and reduces the loan amount you need to borrow."
      },
      {
        question: "How much down payment do I need?",
        answer: "Down payment requirements vary by property type: Homes (10-50%), Commercial properties (25-60%), Cars (10-40%), Investment properties (20-50%). Higher down payments often result in better loan terms."
      },
      {
        question: "What are the benefits of a larger down payment?",
        answer: "Larger down payments reduce your loan amount, lower monthly EMIs, decrease total interest paid, improve loan approval chances, and may help you avoid private mortgage insurance (PMI)."
      },
      {
        question: "Should I use all my savings for down payment?",
        answer: "No, maintain 3-6 months of emergency funds after making the down payment. Also consider closing costs, moving expenses, and immediate property-related costs."
      },
      {
        question: "What is LTV ratio?",
        answer: "Loan-to-Value (LTV) ratio is the percentage of property value financed through a loan. Lower LTV ratios (higher down payments) typically result in better interest rates and loan terms."
      },
      {
        question: "Can I get a loan with zero down payment?",
        answer: "While some special programs offer zero down payment loans, most conventional loans require a minimum down payment. Zero down payment loans often come with higher interest rates and additional costs."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>What factors affect down payment requirements?</Text>
              <Text style={styles.faqAnswer}>
                Down payment requirements are influenced by property type, loan program, credit score, 
                debt-to-income ratio, and lender policies. Government-backed loans often have lower 
                down payment requirements compared to conventional loans.
              </Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderAffordabilityAnalysis()}
      {renderSavingsTimeline()}
      {renderInformation()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    backgroundColor: colors.surface,
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  content: {
    flexDirection: isMobile ? 'column' : 'row',
    padding: 20,
    gap: 20,
    width: '100%',
  },
  leftPanel: {
    flex: isMobile ? 1 : 1.2,
  },
  rightPanel: {
    flex: 1,
    gap: 20,
  },
  calculatorCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background,
  },
  currencySymbol: {
    fontSize: 16,
    color: colors.textSecondary,
    paddingLeft: 12,
  },
  textInput: {
    flex: 1,
    height: 48,
    paddingHorizontal: 12,
    fontSize: 16,
    color: colors.text,
  },
  segmentedControl: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 4,
  },
  segmentButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  segmentButtonActive: {
    backgroundColor: colors.primary,
  },
  segmentButtonText: {
    fontSize: 12,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  segmentButtonTextActive: {
    color: colors.background,
  },
  slider: {
    height: 40,
    marginVertical: 10,
  },
  sliderThumb: {
    backgroundColor: colors.primary,
    width: 20,
    height: 20,
  },
  sliderLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  sliderLabel: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  chartCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    alignItems: 'center',
  },
  chartContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  legendContainer: {
    marginTop: 20,
    width: '100%',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  legendText: {
    fontSize: 14,
    color: colors.text,
  },
  resultsCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  resultRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  resultLabel: {
    fontSize: 14,
    color: colors.textSecondary,
    flex: 1,
  },
  resultValue: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'right',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.primary,
  },
  fullWidthSection: {
    backgroundColor: colors.cardBackground,
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text,
  },
  sectionContent: {
    padding: 20,
  },
  affordabilityCard: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 16,
  },
  affordabilityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  affordabilityTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
  },
  affordabilityBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  affordabilityBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.background,
  },
  affordabilityMetric: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  metricLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  recommendationBox: {
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 4,
  },
  recommendationText: {
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  timelineCard: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 16,
  },
  timelineTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 16,
  },
  progressContainer: {
    marginBottom: 20,
  },
  progressBar: {
    height: 8,
    backgroundColor: colors.border,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  timelineMetrics: {
    gap: 12,
  },
  timelineMetric: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  timelineLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  timelineValue: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  successMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
  },
  successText: {
    fontSize: 14,
    color: colors.success,
    marginLeft: 8,
    flex: 1,
  },
  infoContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 24,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  infoTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
  },
  infoSubtitle: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 16,
  },
  infoText: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
    marginBottom: 16,
  },
  tipBox: {
    flexDirection: 'row',
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  tipText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginLeft: 8,
    flex: 1,
    lineHeight: 18,
  },
  faqContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
  },
  faqContent: {
    marginTop: 20,
  },
  faqItem: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
});

export default DownPaymentCalculatorScreen;